import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";

const BioWritingTips = () => {
  useEffect(() => {
    document.title = "How to Write the Perfect Bio: 15 Proven Tips | NameBioLab";
    
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": "https://namebiolab.lovable.app/blog/perfect-bio-writing-tips#article",
          "headline": "How to Write the Perfect Bio: 15 Proven Tips That Convert Followers",
          "description": "Master the art of bio writing with our comprehensive guide. Includes templates, examples, and strategies for Instagram, Twitter, LinkedIn, and TikTok.",
          "datePublished": "2025-01-05",
          "dateModified": "2025-01-05",
          "author": {
            "@type": "Organization",
            "name": "NameBioLab",
            "url": "https://namebiolab.lovable.app/"
          },
          "publisher": {
            "@type": "Organization",
            "name": "NameBioLab",
            "url": "https://namebiolab.lovable.app/",
            "logo": {
              "@type": "ImageObject",
              "url": "https://namebiolab.lovable.app/favicon.ico"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://namebiolab.lovable.app/blog/perfect-bio-writing-tips"
          },
          "articleSection": "Bio Writing",
          "keywords": "bio writing, Instagram bio, TikTok bio, LinkedIn bio, social media bio, profile bio"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/blog/perfect-bio-writing-tips#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Blog",
              "item": "https://namebiolab.lovable.app/blog"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "Perfect Bio Writing Tips",
              "item": "https://namebiolab.lovable.app/blog/perfect-bio-writing-tips"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link to="/blog">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>
        </Link>

        <article className="prose prose-slate dark:prose-invert max-w-none">
          <header className="mb-8">
            <div className="text-sm text-muted-foreground mb-2">
              Published on January 5, 2025 • 7 min read
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              How to Write the Perfect Bio: 15 Proven Tips That Convert Followers
            </h1>
            <p className="text-xl text-muted-foreground">
              Your bio is your digital elevator pitch. Learn how to craft compelling bios that attract followers, 
              build credibility, and drive action across Instagram, Twitter, TikTok, and LinkedIn.
            </p>
          </header>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Why Your Bio Matters More Than Ever</h2>
            <p>
              In the attention economy, you have approximately 3 seconds to convince someone to follow you. 
              Your bio is the deciding factor. A well-crafted bio increases profile-to-follower conversion by 
              up to 70%. It tells visitors who you are, what value you provide, and why they should care - all 
              in 150 characters or less.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">The Anatomy of a Perfect Bio</h2>
            <p>Every effective bio includes these core elements:</p>
            <ol className="list-decimal pl-6 space-y-3 mt-4">
              <li><strong>Identity/Role:</strong> Who you are in one clear phrase</li>
              <li><strong>Value Proposition:</strong> What you offer or help people achieve</li>
              <li><strong>Credibility Marker:</strong> What makes you qualified or trustworthy</li>
              <li><strong>Personality:</strong> A human touch that makes you relatable</li>
              <li><strong>Call-to-Action:</strong> What you want visitors to do next</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">15 Proven Bio Writing Tips</h2>

            <h3 className="text-2xl font-semibold mb-3 mt-6">1. Lead With Your Strongest Hook</h3>
            <p>
              Put your most compelling credential or value prop first. People skim, so front-load the good stuff.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>❌ "Mom, wife, dog lover, yoga enthusiast, also I'm a nutritionist"</li>
              <li>✅ "Nutritionist helping busy moms lose weight without giving up wine 🍷"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">2. Focus on Benefits, Not Features</h3>
            <p>
              Don't just list what you do - explain how you help people.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>❌ "I post workout videos and meal plans"</li>
              <li>✅ "Transform your body in 12 weeks with proven workouts & meal plans"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">3. Use Numbers for Credibility</h3>
            <p>
              Specific numbers build trust and demonstrate results.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "Helped 10,000+ students land tech jobs"</li>
              <li>✅ "5M+ views teaching Python"</li>
              <li>✅ "Featured in Forbes, TechCrunch, Wired"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">4. Write in Second Person</h3>
            <p>
              Using "you" makes your bio about the reader, not you.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>❌ "I help people grow their Instagram"</li>
              <li>✅ "Grow your Instagram to 10K followers in 90 days"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">5. Add Strategic Emojis</h3>
            <p>
              Emojis break up text, add personality, and draw attention. Use 2-4 relevant emojis.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "🎨 Brand Designer | 💼 Helping startups stand out | 📍 NYC"</li>
              <li>✅ "Coffee addict ☕ | Dog mom 🐕 | Teaching you to code 💻"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">6. Include a Clear Call-to-Action</h3>
            <p>
              Tell people exactly what to do next. Don't make them guess.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "👇 Download my free guide"</li>
              <li>✅ "📧 Join 50K subscribers below"</li>
              <li>✅ "🎯 Get my course here:"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">7. Use Line Breaks for Readability</h3>
            <p>
              Wall-of-text bios are skipped. Use line breaks to create scannable sections.
            </p>
            <div className="bg-muted p-4 rounded-lg mt-2">
              <p className="mb-0">Digital Marketing Expert 📊<br />
              Grew 100+ brands to 7-figures 💰<br />
              Free masterclass 👇</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">8. Showcase Your Unique Angle</h3>
            <p>
              What makes you different from the thousands of others in your niche?
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "The only finance coach who speaks fluent meme"</li>
              <li>✅ "Teaching photography through food (yes, really)"</li>
              <li>✅ "Ex-Google PM turned solopreneur"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">9. Match Your Platform's Vibe</h3>
            <p>
              Different platforms have different tones. Adapt accordingly.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li><strong>LinkedIn:</strong> Professional, credential-focused, results-oriented</li>
              <li><strong>Instagram:</strong> Lifestyle-focused, emoji-friendly, aspirational</li>
              <li><strong>TikTok:</strong> Casual, trendy, self-aware humor</li>
              <li><strong>Twitter:</strong> Witty, concise, opinion-oriented</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">10. Add Keywords for Searchability</h3>
            <p>
              Include industry keywords people might search for.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "Web Developer | JavaScript | React | Node.js"</li>
              <li>✅ "Vegan Chef | Plant-Based Recipes | Meal Prep"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">11. Inject Personality</h3>
            <p>
              Don't be a robot. Show your human side.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>✅ "Helping you adult better (because nobody taught us this stuff)"</li>
              <li>✅ "Professional overthinker turned productivity coach"</li>
              <li>✅ "Probably drinking coffee right now ☕"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">12. Keep It Concise</h3>
            <p>
              Every word should earn its place. Cut ruthlessly.
            </p>
            <ul className="list-none pl-0 mt-2 space-y-1">
              <li>❌ "I am a person who creates content about fashion and style and beauty"</li>
              <li>✅ "Fashion & beauty content creator"</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">13. Update Regularly</h3>
            <p>
              Your bio should evolve with your brand. Update it quarterly or when you:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Hit a new milestone</li>
              <li>Launch a new product/service</li>
              <li>Pivot your content focus</li>
              <li>Get featured somewhere notable</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">14. A/B Test Different Versions</h3>
            <p>
              Try different bio variations and track which drives more follows and clicks.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Test different hooks</li>
              <li>Try different CTAs</li>
              <li>Experiment with emoji placement</li>
              <li>Test benefit-focused vs credential-focused</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">15. Use Link-in-Bio Tools</h3>
            <p>
              Since most platforms only allow one link, use tools like Linktree or Beacons to share multiple links.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Bio Templates by Industry</h2>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Content Creator</h3>
            <div className="bg-muted p-4 rounded-lg">
              <p className="mb-0">[Content Type] Creator 🎥<br />
              [X] followers learning [Topic] 📚<br />
              [Achievement/Credential] ✨<br />
              New [content] every [frequency] 👇</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Coach/Consultant</h3>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="mb-0">[Title] helping [Target Audience] [Achieve Goal] 🎯<br />
              [X] clients | [X]% results 📊<br />
              [Unique Method/Framework] 💡<br />
              Free [Resource] 👇</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">E-commerce/Product</h3>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="mb-0">[Product Description] for [Target Customer] 🛍️<br />
              [Unique Selling Point] ⭐<br />
              [Social Proof] 💯<br />
              Shop now 👇</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Personal Brand</h3>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="mb-0">[Role] @ [Company] 💼<br />
              Sharing [Topics] 📱<br />
              [Personal Touch] 🌟<br />
              [Location] 📍</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Platform-Specific Bio Guidelines</h2>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Instagram Bio (150 characters)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use line breaks liberally</li>
              <li>Include 3-5 emojis</li>
              <li>Add location if relevant</li>
              <li>Use branded hashtag</li>
              <li>Link to landing page</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Twitter/X Bio (160 characters)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Be witty and concise</li>
              <li>Include your expertise</li>
              <li>Add personality/humor</li>
              <li>Link to newsletter or website</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">TikTok Bio (80 characters)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Super short and punchy</li>
              <li>Use trending language</li>
              <li>Add posting schedule</li>
              <li>Include 1-2 emojis max</li>
            </ul>

            <h3 className="text-2xl font-semibold mb-3 mt-6">LinkedIn Bio (220 characters)</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Professional tone</li>
              <li>Lead with credentials</li>
              <li>Include metrics/results</li>
              <li>Add industry keywords</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Common Bio Mistakes to Avoid</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Being too vague:</strong> "Entrepreneur | Influencer | Living my best life" tells us nothing</li>
              <li><strong>Too many hashtags:</strong> More than 1-2 branded hashtags looks spammy</li>
              <li><strong>No clear niche:</strong> Trying to appeal to everyone appeals to no one</li>
              <li><strong>Missing CTA:</strong> If you don't tell people what to do, they won't do anything</li>
              <li><strong>Outdated info:</strong> Dead links or old achievements hurt credibility</li>
              <li><strong>Over-explaining:</strong> Save the full story for your About page</li>
              <li><strong>Emoji overload:</strong> More than 5 emojis becomes hard to read</li>
            </ul>
          </section>

          <section className="mb-8 bg-card rounded-xl p-6">
            <h2 className="text-3xl font-semibold mb-4">Generate Your Perfect Bio in Seconds</h2>
            <p className="mb-4">
              Struggling to write your bio? Our AI-powered bio generator creates compelling, platform-optimized 
              bios tailored to your niche, personality, and goals. Get 5 unique bio options in seconds.
            </p>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary">
                Try Free Bio Generator
              </Button>
            </Link>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Real Bio Examples That Work</h2>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Fitness Coach</h3>
            <div className="bg-muted p-4 rounded-lg">
              <p className="mb-0">💪 Transform your body in 90 days<br />
              📊 10K+ transformations & counting<br />
              🎓 Certified Nutritionist + PT<br />
              👇 Free meal plan below</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Tech YouTuber</h3>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="mb-0">📱 Unboxing the future of tech<br />
              🎥 New reviews every Tuesday<br />
              📧 2M subscribers | As seen in MKBHD<br />
              Latest video 👇</p>
            </div>

            <h3 className="text-2xl font-semibold mb-3 mt-6">Freelance Designer</h3>
            <div className="bg-muted p-4 rounded-lg mt-4">
              <p className="mb-0">🎨 Brand Designer for ambitious startups<br />
              🚀 Helped 50+ companies raise $100M+<br />
              💼 Currently: Open for Q2 projects<br />
              📩 DM for rates</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-semibold mb-4">Final Thoughts</h2>
            <p>
              Your bio is one of the highest-leverage pieces of content you'll write. A strong bio works 24/7 
              to attract followers, build credibility, and drive action. Invest time in crafting it well, then 
              test and refine based on results.
            </p>
            <p>
              Remember: your bio isn't about you - it's about what you can do for your audience. Focus on their 
              needs, speak their language, and make it crystal clear why they should follow you. The followers 
              will come.
            </p>
          </section>
        </article>
      </div>
    </div>
  );
};

export default BioWritingTips;
